// Premium 12-Week Roadmap Timeline - Nordic Minimalist Design
import React from 'react';
import { Search, Palette, Code, Rocket, CheckCircle2, FileText, Users, Zap } from 'lucide-react';
import { useScrollReveal } from '@/hooks/use-scroll-reveal';

interface Phase {
  weeks: string;
  title: string;
  subtitle: string;
  description: string;
  icon: React.ReactNode;
  deliverables: string[];
  ownership: string;
  color: string;
}

const phases: Phase[] = [
  {
    weeks: 'Week 1-2',
    title: 'Discovery',
    subtitle: 'Goals validated',
    description: 'Map goals, validate assumptions, define MVP scope with stakeholder alignment.',
    icon: <Search className="w-6 h-6" />,
    deliverables: ['Requirements document', 'User stories', 'Technical scope'],
    ownership: 'We lead, you validate',
    color: 'from-cyan-500 to-blue-500',
  },
  {
    weeks: 'Week 3-5',
    title: 'Design',
    subtitle: 'Screens approved',
    description: 'Create wireframes, high-fidelity designs, and interactive prototypes.',
    icon: <Palette className="w-6 h-6" />,
    deliverables: ['Wireframes', 'UI/UX designs', 'Interactive prototype'],
    ownership: 'We design, you approve',
    color: 'from-blue-500 to-violet-500',
  },
  {
    weeks: 'Week 6-10',
    title: 'Development',
    subtitle: 'MVP built',
    description: 'Build core features with weekly demos on staging environment.',
    icon: <Code className="w-6 h-6" />,
    deliverables: ['Staging environment', 'Core features', 'Weekly demos'],
    ownership: 'We build, you test',
    color: 'from-violet-500 to-purple-500',
  },
  {
    weeks: 'Week 11-12',
    title: 'Launch',
    subtitle: 'Live & optimized',
    description: 'Deploy to production with analytics, monitoring, and documentation handover.',
    icon: <Rocket className="w-6 h-6" />,
    deliverables: ['Production deployment', 'Analytics setup', 'Handover docs'],
    ownership: 'We launch, you own',
    color: 'from-purple-500 to-primary',
  },
];

const PhaseCard = ({ phase, index, isVisible }: { phase: Phase; index: number; isVisible: boolean }) => {
  const isLast = index === phases.length - 1;
  
  return (
    <div 
      className="relative flex flex-col"
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
        transition: `all 0.7s cubic-bezier(0.16, 1, 0.3, 1) ${index * 150}ms`,
      }}
    >
      {/* Timeline connector - Desktop */}
      {!isLast && (
        <div className="hidden lg:block absolute top-[60px] left-[calc(100%+8px)] w-[calc(100%-16px)] h-[2px]">
          <div 
            className="h-full bg-gradient-to-r from-white/20 to-white/5"
            style={{
              transform: isVisible ? 'scaleX(1)' : 'scaleX(0)',
              transformOrigin: 'left',
              transition: `transform 0.8s cubic-bezier(0.16, 1, 0.3, 1) ${(index + 1) * 200}ms`,
            }}
          />
          {/* Animated dot */}
          <div 
            className="absolute right-0 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-primary"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'scale(1)' : 'scale(0)',
              transition: `all 0.4s cubic-bezier(0.16, 1, 0.3, 1) ${(index + 1) * 300}ms`,
            }}
          />
        </div>
      )}
      
      {/* Card */}
      <div 
        className="group relative flex flex-col h-full rounded-2xl border border-white/[0.08] bg-[#111111] p-6 lg:p-8 transition-all duration-500 hover:border-primary/30 hover:-translate-y-2"
        style={{
          boxShadow: '0 4px 24px -8px rgba(0, 0, 0, 0.5)',
        }}
      >
        {/* Gradient border effect on hover */}
        <div 
          className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${phase.color} opacity-0 group-hover:opacity-[0.05] transition-opacity duration-500`}
        />
        <div 
          className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
          style={{
            boxShadow: '0 8px 32px -12px hsl(var(--primary) / 0.2)',
          }}
        />
        
        {/* Week badge */}
        <div className="flex items-center justify-between mb-4">
          <span className="text-xs font-mono font-semibold text-primary uppercase tracking-wider">
            {phase.weeks}
          </span>
          <div 
            className={`w-10 h-10 rounded-xl bg-gradient-to-br ${phase.color} flex items-center justify-center text-white shadow-lg group-hover:scale-110 transition-transform duration-300`}
            style={{
              boxShadow: `0 4px 20px -4px hsl(var(--primary) / 0.3)`,
            }}
          >
            {phase.icon}
          </div>
        </div>
        
        {/* Title & Subtitle */}
        <h3 className="text-xl font-bold text-white mb-1">{phase.title}</h3>
        <p className="text-sm text-primary font-medium mb-3">{phase.subtitle}</p>
        
        {/* Description */}
        <p className="text-sm text-white/60 leading-relaxed mb-6">
          {phase.description}
        </p>
        
        {/* Deliverables */}
        <div className="flex-1">
          <p className="text-xs font-semibold text-white/40 uppercase tracking-wider mb-3">
            Deliverables
          </p>
          <ul className="space-y-2.5">
            {phase.deliverables.map((item, i) => (
              <li key={i} className="flex items-center gap-2.5 text-sm text-white/70 group-hover:text-white/80 transition-colors">
                <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                  <CheckCircle2 className="w-3.5 h-3.5 text-primary" />
                </div>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
        
        {/* Ownership */}
        <div className="mt-6 pt-4 border-t border-white/[0.06]">
          <p className="text-xs text-white/40 flex items-center gap-2">
            <Users className="w-3.5 h-3.5" />
            {phase.ownership}
          </p>
        </div>
      </div>
    </div>
  );
};

export const TwelveWeekRoadmap = () => {
  const { isVisible, elementRef } = useScrollReveal({ threshold: 0.1 });
  
  return (
    <section 
      ref={elementRef}
      className="relative py-24 lg:py-32 bg-[#0a0a0a] overflow-hidden"
    >
      {/* Subtle background glow */}
      <div 
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse, hsl(var(--primary) / 0.03) 0%, transparent 70%)',
        }}
      />
      
      <div className="container-professional relative z-10">
        {/* Section Header */}
        <div 
          className="text-center mb-16 lg:mb-20"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
          }}
        >
          <span className="inline-flex items-center gap-2 text-xs font-semibold text-primary uppercase tracking-wider mb-4">
            <Zap className="w-4 h-4" />
            The Roadmap
          </span>
          <h2 className="text-3xl lg:text-5xl font-bold text-white mb-4">
            From Concept to Launch in{' '}
            <span className="text-primary">12 Weeks</span>
          </h2>
          <p className="text-lg text-white/60 max-w-2xl mx-auto">
            A proven, transparent process. You know exactly what happens each week, 
            what you'll receive, and who owns what.
          </p>
        </div>
        
        {/* Timeline Progress Bar - Desktop */}
        <div 
          className="hidden lg:block relative mb-12"
          style={{
            opacity: isVisible ? 1 : 0,
            transition: 'opacity 0.6s cubic-bezier(0.16, 1, 0.3, 1) 200ms',
          }}
        >
          <div className="h-1 bg-white/[0.06] rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-primary via-violet-500 to-purple-500 rounded-full"
              style={{
                width: isVisible ? '100%' : '0%',
                transition: 'width 1.5s cubic-bezier(0.16, 1, 0.3, 1) 400ms',
              }}
            />
          </div>
          {/* Week markers */}
          <div className="flex justify-between mt-2 px-2">
            {['Week 1', 'Week 3', 'Week 6', 'Week 11', 'Week 12'].map((week, i) => (
              <span 
                key={week} 
                className="text-xs text-white/30 font-mono"
                style={{
                  opacity: isVisible ? 1 : 0,
                  transition: `opacity 0.4s ease ${500 + i * 100}ms`,
                }}
              >
                {week}
              </span>
            ))}
          </div>
        </div>
        
        {/* Phase Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-4">
          {phases.map((phase, index) => (
            <PhaseCard 
              key={phase.title} 
              phase={phase} 
              index={index} 
              isVisible={isVisible}
            />
          ))}
        </div>
        
        {/* Bottom CTA */}
        <div 
          className="mt-16 text-center"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 0.6s cubic-bezier(0.16, 1, 0.3, 1) 800ms',
          }}
        >
          <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-white/[0.03] border border-white/[0.08]">
            <FileText className="w-5 h-5 text-primary" />
            <span className="text-sm text-white/70">
              Every project includes a detailed timeline document on day one
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};
